import debounce from 'lodash/debounce';
import axios from 'axios';
import APP_CONSTANTS from '@ordermentum/ui-constants';

type Callback = {
  (type: string): any;
};

/**
 * @description This is a debouncer factory function which takes a callback and calls that callback after fetching
 * a card type for the card number passed to the function this factory returns.
 * @example 2345 XXXX => will give back mastercard and the callback is called with "mastercard" as the return type
 *
 * @param {Callback} callback Callback called after fetching credit card type
 * @returns {(((cardNumber: String) => Promise<string>) & Cancelable)}
 */
// TODO(ish): Integrate SWR. Move this function.
export const createCreditCardTypeDebouncer = (callback: Callback) =>
  debounce(
    async (cardNumber: String) => {
      try {
        const response = await axios.post(
          '/v1/payment-methods/card-type/',
          { cardNumber },
          {
            responseType: 'json',
            timeout: 20000,
          }
        );
        return callback(response.data ? response.data.toLowerCase() : '');
      } catch (err) {
        return callback('');
      }
    },
    500,
    { maxWait: 600 }
  );

export const getCCExpiryYear = expiryDate => {
  if (expiryDate.length > APP_CONSTANTS.EXPIRY_YEAR_POSITION) {
    return `20${expiryDate.substr(
      APP_CONSTANTS.EXPIRY_YEAR_POSITION,
      expiryDate.length
    )}`;
  }
  return '20';
};

/**
 * Returns true if the given card expiry (month/year) is in the past.
 * Card expiry is treated as end of the given month (e.g. 01/2026 = valid until end of Jan 2026).
 *
 * @param month - Expiry month (1-12)
 * @param year - Expiry year (full year, e.g. 2026)
 */
export const isCreditCardExpired = (month: number, year: number): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 0-11 -> 1-12

  if (year > currentYear) return false;
  if (year === currentYear && month >= currentMonth) return false;

  return true;
};
